import { JQueryInput } from "./types"
import { registerEvents } from "./skyward.ui.events";
import { encodeHtml } from "./skyward.base.extensions";

interface MultiSelectHiddenData
{
    allowCustomValues: boolean;
    forId: string;
    searchParameters: string[];
    originalValue: Array<string | number | boolean> | string | number | boolean | undefined;
}

interface MultiSelectData
{
    headerTextSome: string;
    headerTextNone: string;
}

const skyward = window.skyward;
skyward.multipleSelectBox = skyward.multipleSelectBox || {};

const events =
{
    init,
    click,
    addCustomClick,
    enterKeyDown
};

registerEvents("multipleSelectBox", events);

const classNames =
{
    multipleSelectBox: "multipleSelectBox",
    multipleSelectOptionCheckbox: "multipleSelectOptionCheckbox",
    multipleSelectHeader: "multipleSelectHeader",
    multiSelectBackingData: "multiSelectBackingData",
    addCustomValueButton: "addCustomValueButton",
    customValue: "customValue",
    customValueTemplate: "customValueTemplate",
    multipleSelectOption: "multipleSelectOption"
};

skyward.multipleSelectBox.populate = populate;
export function populate(backingData: JQuery<HTMLElement>, value: string | string[] | undefined, triggerChange: boolean): void
{
    const multiSelectBox = backingData.closest(`.${classNames.multipleSelectBox}`);
    const options = multiSelectBox.find(`.${classNames.multipleSelectOptionCheckbox}`);

    const data = (typeof value != "object") ? JSON.parse(value as string) : (value ?? []);

    // data should always be an array, but keeping Object.keys for backwards compatibility
    const searchParameters = Array.isArray(data) ? data : Object.keys(data);

    updateHiddenField(backingData, searchParameters, triggerChange);

    const optionsByValue = new Map<string, JQuery<HTMLElement>>();
    $.each(options, function ()
    {
        const option = $(this);
        option.prop("checked", false);
        optionsByValue.set(option.val() as string, option);
    });

    const list = $(`#${backingData.data("forId")}`);

    for (const searchParameter of searchParameters)
    {
        if (optionsByValue.has(searchParameter))
        {
            (optionsByValue.get(searchParameter) as JQuery<HTMLElement>).prop("checked", true);
        }
        else
        {
            addCustomValue(list, searchParameter);
        }
    }
}

skyward.multipleSelectBox.getValue = getValue;
export function getValue(element: JQuery<HTMLElement>)
{
    return element.data().searchParameters.map((v: string | number | boolean) => encodeHtml(v));
}

skyward.multipleSelectBox.getOriginalValue = getOriginalValue;
export function getOriginalValue(element: JQuery<HTMLElement>)
{
    const originalValue = (element.data() as MultiSelectHiddenData).originalValue;
    if (!Array.isArray(originalValue))
    {
        const originalValues: string[] = [];
        return originalValues;
    }

    return originalValue.map((v) => encodeHtml(v));
}

skyward.multipleSelectBox.clearValue = clearValue;
export function clearValue(backingData: JQuery<HTMLElement>, triggerChange: boolean)
{
    populate(backingData, [], triggerChange);
}

function init(this: JQueryInput)
{
    const multiSelectBox = $(this);
    multiSelectBox.on("change", ".customValue", removeCustomValue);

    if (!multiSelectBox.attr("aria-label") && !multiSelectBox.attr("aria-labelledby"))
    {
        const label = multiSelectBox.findLabel();

        if (label.length > 0)
        {
            const id = label.addIds().attr("id")!;
            multiSelectBox.attr("aria-labelledby", id);
        }
    }
}

function click(this: JQueryInput)
{
    const multiSelectBox = $(this);
    const multiSelectBoxData = multiSelectBox.data() as MultiSelectData;
    const selectedOptions = multiSelectBox.find(`.${classNames.multipleSelectOptionCheckbox}:checked:visible`);
    const multiSelectHeader = multiSelectBox.find(`.${classNames.multipleSelectHeader}`);
    multiSelectHeader.text(selectedOptions.length > 0 ? multiSelectBoxData.headerTextSome : multiSelectBoxData.headerTextNone);

    const backingData = multiSelectBox.find(`.${classNames.multiSelectBackingData}`);
    const searchParameters: string[] = [];
    selectedOptions.each(function ()
    {
        const selectedOption = $(this);
        searchParameters.push(selectedOption.val() as string);
    });
    updateHiddenField(backingData, searchParameters, true);
}

function addCustomClick(this: JQueryInput)
{
    const button = $(this);
    const list = $("#" + button.data("forId"));
    const input = button.prev();
    const userValue = input.val();

    addCustomValue(list, userValue);

    input.val("").trigger("focus");
}

function addCustomValue(list: JQuery<HTMLElement>, value: string | number | string[] | undefined)
{
    const template = list.find(`.${classNames.customValueTemplate}`);

    if (list.length && template.length && value)
    {
        value = value.toString();

        for (const existingOption of list.find(`.${classNames.multipleSelectOption} input`))
        {
            if ($(existingOption).val() == value)
            {
                return;
            }
        }

        const newEntry = template.clone().removeClass(classNames.customValueTemplate).addClass(classNames.customValue);
        newEntry.find("input").val(value).prop("checked", true);
        newEntry.find("label").text(value);

        const hidden = list.find(`.${classNames.multiSelectBackingData}`);
        const hiddenData = hidden.data() as MultiSelectHiddenData;
        const searchParameters = hiddenData.searchParameters;
        searchParameters.push(value);
        updateHiddenField(hidden, searchParameters, true);

        newEntry.appendTo(list).show();
    }
}

function removeCustomValue(this: HTMLElement)
{
    const jqThis = $(this);

    if (!jqThis.find(".checkbox").prop("checked"))
    {
        const valToRemove = jqThis.find(`.${classNames.multipleSelectOptionCheckbox}`).val() as string;
        let nextCheckbox = jqThis.next(`.${classNames.multipleSelectOption}:not(.${classNames.customValueTemplate})`);
        if (!nextCheckbox.length)
        {
            nextCheckbox = jqThis.prev(`.${classNames.multipleSelectOption}:not(.${classNames.customValueTemplate})`);
        }
        nextCheckbox.find("input").trigger("focus");

        const hidden = jqThis.parent(`.${classNames.multipleSelectBox}`).find(`.${classNames.multiSelectBackingData}`);
        const hiddenData = hidden.data() as MultiSelectHiddenData;
        let searchParameters = hiddenData.searchParameters;
        searchParameters = searchParameters.filter((v) => v != valToRemove);
        updateHiddenField(hidden, searchParameters);

        jqThis.remove();
    }
}

function updateHiddenField(hiddenInput: JQuery<HTMLElement>, searchParameters: string[], triggerChange: boolean = false)
{
    hiddenInput.data("searchParameters", searchParameters);
    if (triggerChange)
    {
        hiddenInput.trigger("change");
    }
}

function enterKeyDown(this: JQueryInput)
{
    const element = $(this);
    if (element.hasClass("bound"))
    {
        return;
    }

    const input = element.find("input");
    input.on("keydown", function (event)
    {
        const eventKeys = skyward.keys.getKeyNamesFromEvent(event);
        if (eventKeys.ENTER)
        {
            input.next().click();
        }
    });

    element.addClass("bound");
}
